import React, { useMemo, useState } from 'react';
import type { AxiosInstance } from 'axios';
import SimCard from '../../sim/ui/SimCard';
import { SimBody, SimLabel } from '../../sim/ui/SimText';
import api from '../../services/api';
import BucketSortTask from './BucketSortTask';

type SimComponent = {
  rowKey: string;
  rowOrder: number;
  rowType: string;
  payload: Record<string, unknown>;
};

type ScreenRendererProps = {
  components: SimComponent[];
  answers?: Record<string, unknown>;
  onAnswersChange?: (answers: Record<string, unknown>) => void;
  onTaskPresenceChange?: (hasTaskInputs: boolean) => void;
  onInputActivity?: (activity: { type: 'paste' | 'change'; rowKey: string; inputType: 'NumericEntry' | 'other' }) => void;
};

export type ScreenFetchParams = {
  attemptId?: string;
  variantCode?: string;
  screenCode: string;
  signal?: AbortSignal;
};

export const fetchScreen = async (
  params: ScreenFetchParams,
  client: AxiosInstance = api
) => {
  const screenCode = params.screenCode?.trim();
  if (!screenCode) {
    throw new Error('screenCode is required');
  }

  if (params.attemptId && params.attemptId.trim().length > 0) {
    const response = await client.get(`/sim/attempts/${params.attemptId}/screens/${screenCode}`, {
      signal: params.signal,
    });
    return response.data;
  }

  if (params.variantCode && params.variantCode.trim().length > 0) {
    const response = await client.get(`/sim/simulation/${params.variantCode}/${screenCode}`, {
      signal: params.signal,
    });
    return response.data;
  }

  throw new Error('Either attemptId or variantCode is required');
};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '').trim();

export const isPackExcerptLabel = (value: unknown): boolean => {
  return asString(value).toLowerCase() === 'pack excerpt';
};

const normalizeOptions = (value: unknown): Array<{ key: string; text: string }> => {
  if (!Array.isArray(value)) return [];
  return value
    .map((option, index) => {
      if (typeof option === 'string') {
        const key = option.trim() || `OPT_${index + 1}`;
        return { key, text: key };
      }
      if (!option || typeof option !== 'object' || Array.isArray(option)) return null;
      const obj = option as Record<string, unknown>;
      const key = String(obj.key ?? obj.id ?? `OPT_${index + 1}`).trim();
      const text = String(obj.label ?? obj.text ?? key).trim();
      if (!key) return null;
      return { key, text: text || key };
    })
    .filter((option): option is { key: string; text: string } => Boolean(option));
};

type BucketSortEntity = {
  key: string;
  label: string;
  order?: number;
};

const normalizeBucketSortEntities = (value: unknown): BucketSortEntity[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => {
        if (typeof entry === 'string') {
          return {
            key: entry.trim() || `item_${index + 1}`,
            label: entry.trim() || `item_${index + 1}`,
            order: index,
          };
        }
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
        const item = entry as Record<string, unknown>;
        const key =
          String(
            item.key ??
              item.id ??
              item.itemKey ??
              item.bucketKey ??
              `${item.label ? String(item.label) : `item_${index + 1}`}`
          ).trim() || `item_${index + 1}`;
        const label = String(item.label ?? item.text ?? item.name ?? key).trim() || key;
        return {
          key,
          label,
          order: Number.isFinite(Number(item.order)) ? Number(item.order) : index,
        };
      })
      .filter((entry): entry is BucketSortEntity => Boolean(entry && entry.key));
  }

  if (!value || typeof value !== 'object') return [];
  return Object.entries(value as Record<string, unknown>)
    .map(([key, entry], index) => {
      const normalizedKey = String(key || '').trim();
      if (!normalizedKey) return null;
      if (typeof entry === 'string') {
        const label = entry.trim() || normalizedKey;
        return {
          key: normalizedKey,
          label,
          order: index,
        };
      }
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return {
          key: normalizedKey,
          label: normalizedKey,
          order: index,
        };
      }
      const item = entry as Record<string, unknown>;
      return {
        key: normalizedKey,
        label: String(item.label ?? item.text ?? item.name ?? normalizedKey).trim() || normalizedKey,
        order: Number.isFinite(Number(item.order)) ? Number(item.order) : index,
      };
    })
    .filter((entry): entry is BucketSortEntity => Boolean(entry && entry.key));
};

const normalizeBucketSortAssignments = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, string>>((acc, [itemKey, bucketKey]) => {
    const normalizedItemKey = String(itemKey || '').trim();
    const normalizedBucketKey = typeof bucketKey === 'string' ? bucketKey.trim() : '';
    if (!normalizedItemKey || !normalizedBucketKey) return acc;
    acc[normalizedItemKey] = normalizedBucketKey;
    return acc;
  }, {});
};

const warnedBucketSortFallbackKeys = new Set<string>();
const warnedBucketSortInvalidKeys = new Set<string>();

export const resetBucketSortWarningsForTests = (): void => {
  warnedBucketSortFallbackKeys.clear();
  warnedBucketSortInvalidKeys.clear();
};

const ScreenRenderer: React.FC<ScreenRendererProps> = ({
  components,
  answers,
  onAnswersChange,
  onTaskPresenceChange,
  onInputActivity,
}) => {
  const [numericAnswers, setNumericAnswers] = useState<Record<string, string>>({});
  const [singleAnswers, setSingleAnswers] = useState<Record<string, string>>({});
  const [picklistAnswers, setPicklistAnswers] = useState<Record<string, string>>({});
  const [multiSelectAnswers, setMultiSelectAnswers] = useState<Record<string, string[]>>({});
  const [matchingAnswers, setMatchingAnswers] = useState<
    Record<string, { pairs: Array<{ leftId: string; rightId: string }> }>
  >({});
  const [bucketSortAnswers, setBucketSortAnswers] = useState<Record<string, Record<string, string>>>({});
  const [selectedLeftByTask, setSelectedLeftByTask] = useState<Record<string, string>>({});

  const hasTaskInputs = useMemo(
    () =>
      components.some((component) => {
        if (component.rowType !== 'TASK') return false;
        const taskType = typeof component.payload.taskType === 'string' ? component.payload.taskType : '';
        return (
          taskType === 'NumericEntry' ||
          taskType === 'SingleChoice' ||
          taskType === 'Picklist' ||
          taskType === 'MultiSelect' ||
          taskType === 'MatchingPairs' ||
          taskType === 'BucketSort'
        );
      }),
    [components]
  );

  React.useEffect(() => {
    if (onTaskPresenceChange) onTaskPresenceChange(hasTaskInputs);
  }, [hasTaskInputs, onTaskPresenceChange]);

  const commitAnswer = (rowKey: string, value: unknown, fallbackSetter?: (value: unknown) => void) => {
    if (onAnswersChange) {
      onAnswersChange({
        ...(answers || {}),
        [rowKey]: value,
      });
      return;
    }
    fallbackSetter?.(value);
  };

  const rendered: React.ReactNode[] = [];
  for (let i = 0; i < components.length; i += 1) {
    const component = components[i];

    if (component.rowType === 'ANSWERKEY') {
      continue;
    }
    if (component.rowType === 'MATCH_KEY' || /_OPTION$/.test(component.rowType)) {
      continue;
    }

    if (component.rowType === 'TEXT') {
      const text = asString(component.payload.text);
      const emphasis = asString(component.payload.emphasis);
      const label = asString(component.payload.label);
      rendered.push(
        <SimBody key={component.rowKey}>
          {label && !isPackExcerptLabel(label) ? (
            <p className="mb-2 text-xs uppercase tracking-[0.14em] text-txt-muted">{label}</p>
          ) : null}
          {text}
          {emphasis ? <span className="ml-2 font-medium text-txt-primary">{emphasis}</span> : null}
        </SimBody>
      );
      continue;
    }

    if (component.rowType === 'DISPLAY') {
      const title = asString(component.payload.title);
      const displayLabel = asString(component.payload.label) || title || asString(component.payload.text);
      rendered.push(
        <SimCard key={component.rowKey} className="border border-line bg-surface-header p-5 sm:p-6">
          {isPackExcerptLabel(displayLabel) ? null : <SimLabel>Pack Excerpt</SimLabel>}
          {title ? (
            <p className="mt-2 text-base font-semibold text-txt-primary">{title}</p>
          ) : null}
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-txt-secondary/95">{asString(component.payload.text)}</p>
          {asString(component.payload.emphasis) ? (
            <p className="mt-2 text-xs uppercase tracking-[0.14em] text-txt-muted">{asString(component.payload.emphasis)}</p>
          ) : null}
        </SimCard>
      );
      continue;
    }

    if (component.rowType === 'PRACTICE') {
      rendered.push(
        <SimCard key={component.rowKey} className="border border-line bg-surface-header p-5 sm:p-6">
          <SimLabel>Practice</SimLabel>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-txt-secondary">{asString(component.payload.text)}</p>
        </SimCard>
      );
      continue;
    }

    if (component.rowType === 'FIELD') {
      rendered.push(
        <SimCard key={component.rowKey} className="p-4 sm:p-5">
          <div className="grid gap-2 sm:grid-cols-[minmax(10rem,16rem)_1fr] sm:items-start">
            <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">{asString(component.payload.label) || component.rowKey}</p>
            <p className="text-sm text-txt-primary whitespace-pre-wrap">{asString(component.payload.inputType) || asString(component.payload.value)}</p>
          </div>
        </SimCard>
      );
      continue;
    }

    if (component.rowType === 'CARD') {
      const title = asString(component.payload.title) || asString(component.payload.text) || component.rowKey;
      const body = asString(component.payload.body) || asString(component.payload.details);
      const emphasis = asString(component.payload.emphasis);
      rendered.push(
        <SimCard key={component.rowKey} className="p-4 sm:p-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-input border border-line bg-surface-page/40 p-3">
              {!isPackExcerptLabel(title) ? <p className="text-sm font-semibold text-txt-primary">{title}</p> : null}
              {body ? <p className="mt-1 text-sm text-txt-muted whitespace-pre-wrap">{body}</p> : null}
              {emphasis ? <p className="mt-2 text-xs uppercase tracking-[0.14em] text-txt-muted">{emphasis}</p> : null}
            </div>
          </div>
        </SimCard>
      );
      continue;
    }

    if (component.rowType === 'CLAUSE_TILE') {
      rendered.push(
        <SimCard key={component.rowKey} className="p-4 sm:p-5">
          <div className="rounded-input border border-line bg-surface-page/40 px-4 py-3 text-sm text-txt-primary">
            {asString(component.payload.text) || asString(component.payload.label) || component.rowKey}
          </div>
        </SimCard>
      );
      continue;
    }

    if (component.rowType === 'TERM_FIELD') {
      const label = asString(component.payload.label) || component.rowKey;
      const format = asString(component.payload.format);
      const value = asString(component.payload.value);
      rendered.push(
        <SimCard key={component.rowKey} className="p-4 sm:p-5">
          <label className="block text-xs uppercase tracking-[0.14em] text-txt-muted">{label}</label>
          {value ? (
            <p className="mt-2 text-sm text-txt-primary">{value}</p>
          ) : (
            <input
              type={format.toLowerCase().includes('num') ? 'number' : 'text'}
              disabled
              value=""
              placeholder={format || 'Value'}
              className="mt-2 w-full rounded-input border border-line bg-surface-page/50 px-3 py-2 text-sm text-txt-muted placeholder:text-txt-muted"
            />
          )}
        </SimCard>
      );
      continue;
    }

    if (component.rowType === 'TABLE') {
      const tableRows: SimComponent[] = [component];
      let j = i + 1;
      while (j < components.length && components[j].rowType === 'TABLE') {
        tableRows.push(components[j]);
        j += 1;
      }
      i = j - 1;
      const columns = ['label', 'value', 'notes', 'extra']
        .map((key) => ({ key, title: key === 'label' ? 'Label' : key === 'value' ? 'Value' : key === 'notes' ? 'Notes' : 'Extra' }))
        .filter((column) => tableRows.some((row) => asString(row.payload[column.key]).length > 0));
      rendered.push(
        <SimCard key={`table:${component.rowKey}`} className="p-4 sm:p-5">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm text-txt-primary">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} className="border-b border-line px-3 py-2 text-left text-xs uppercase tracking-[0.12em] text-txt-muted">
                      {column.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={row.rowKey} className="border-b border-line-subtle align-top transition-colors duration-150 hover:bg-surface-hover last:border-b-0">
                    {columns.map((column) => (
                      <td key={`${row.rowKey}:${column.key}`} className="px-3 py-2 whitespace-pre-wrap text-txt-secondary/95">
                        {asString(row.payload[column.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SimCard>
      );
      continue;
    }

    if (component.rowType === 'TASK') {
      const taskType = typeof component.payload.taskType === 'string' ? component.payload.taskType : '';

      if (taskType === 'NumericEntry') {
        const prompt = asString(component.payload.prompt);
        rendered.push(
          <SimCard key={component.rowKey} className="bg-surface-header p-5 sm:p-6">
            <SimLabel>Numeric Task</SimLabel>
            <p className="mt-3 text-base font-medium text-txt-primary">{prompt}</p>
            <input
              type="text"
              inputMode="decimal"
              value={
                String(
                  (answers && (answers[component.rowKey] as string | number | undefined)) ??
                    numericAnswers[component.rowKey] ??
                    ''
                )
              }
              onChange={(event) => {
                const nextValue = event.target.value;
                onInputActivity?.({
                  type: 'change',
                  rowKey: component.rowKey,
                  inputType: 'NumericEntry',
                });
                commitAnswer(component.rowKey, nextValue, (value) =>
                  setNumericAnswers((prev) => ({ ...prev, [component.rowKey]: String(value) }))
                );
              }}
              onPaste={() =>
                onInputActivity?.({
                  type: 'paste',
                  rowKey: component.rowKey,
                  inputType: 'NumericEntry',
                })
              }
              placeholder="Enter answer"
              className="mt-4 w-full rounded-input border border-line bg-surface-page/60 px-4 py-2.5 text-txt-primary placeholder:text-txt-muted focus:outline-none focus:ring-2 focus:ring-brand-600/20 focus:border-line-active"
            />
          </SimCard>
        );
        continue;
      }

      if (taskType === 'SingleChoice') {
        const options = normalizeOptions(component.payload.options);
        const selected = String((answers?.[component.rowKey] as string | undefined) ?? singleAnswers[component.rowKey] ?? '');
        rendered.push(
          <SimCard key={component.rowKey} className="bg-surface-header p-5 sm:p-6">
            <SimLabel>Single-choice Task</SimLabel>
            <p className="mt-3 text-base font-medium text-txt-primary">{asString(component.payload.prompt) || 'Select one option'}</p>
            <div className="mt-4 space-y-2">
              {options.map((option) => (
                <label
                  key={`${component.rowKey}:${option.key}`}
                  className={`flex items-start gap-3 rounded-input border px-3 py-2 text-sm ${
                    selected === option.key
                      ? 'border-brand-700 text-txt-primary ring-1 ring-brand-700/20 shadow-sm'
                      : 'border-line bg-surface-card text-txt-secondary hover:border-line-active'
                  }`}
                >
                  <input
                    type="radio"
                    name={component.rowKey}
                    checked={selected === option.key}
                    onChange={() => {
                      onInputActivity?.({ type: 'change', rowKey: component.rowKey, inputType: 'other' });
                      commitAnswer(component.rowKey, option.key, (value) =>
                        setSingleAnswers((prev) => ({ ...prev, [component.rowKey]: String(value) }))
                      );
                    }}
                    className="mt-0.5 h-4 w-4 border-line bg-surface-card text-brand-700 accent-brand-700 focus:ring-brand-700"
                  />
                  <span>{option.text}</span>
                </label>
              ))}
            </div>
          </SimCard>
        );
        continue;
      }

      if (taskType === 'Picklist') {
        const options = normalizeOptions(component.payload.options);
        const selected = String((answers?.[component.rowKey] as string | undefined) ?? picklistAnswers[component.rowKey] ?? '');
        rendered.push(
          <SimCard key={component.rowKey} className="bg-surface-header p-5 sm:p-6">
            <SimLabel>Picklist Task</SimLabel>
            <p className="mt-3 text-base font-medium text-txt-primary">{asString(component.payload.prompt) || 'Select one option'}</p>
            <select
              value={selected}
              onChange={(event) => {
                onInputActivity?.({ type: 'change', rowKey: component.rowKey, inputType: 'other' });
                commitAnswer(component.rowKey, event.target.value, (value) =>
                  setPicklistAnswers((prev) => ({ ...prev, [component.rowKey]: String(value) }))
                );
              }}
              className="mt-4 w-full rounded-input border border-line bg-surface-page/60 px-3 py-2.5 text-sm text-txt-primary focus:border-line-active focus:outline-none focus:ring-2 focus:ring-brand-600/20"
            >
              <option value="">Select...</option>
              {options.map((option) => (
                <option key={`${component.rowKey}:${option.key}`} value={option.key}>
                  {option.text}
                </option>
              ))}
            </select>
          </SimCard>
        );
        continue;
      }

      if (taskType === 'MultiSelect') {
        const options = normalizeOptions(component.payload.options);
        const selectExactlyRaw =
          typeof component.payload.selectExactly === 'number'
            ? component.payload.selectExactly
            : typeof component.payload.selectExactly === 'string'
              ? Number(component.payload.selectExactly)
              : null;
        const selectExactly =
          typeof selectExactlyRaw === 'number' && Number.isInteger(selectExactlyRaw) && selectExactlyRaw > 0
            ? selectExactlyRaw
            : null;
        const currentSelectionRaw = (answers && answers[component.rowKey]) ?? multiSelectAnswers[component.rowKey] ?? [];
        const currentSelection = Array.isArray(currentSelectionRaw)
          ? currentSelectionRaw.map((item) => String(item)).filter(Boolean)
          : [];
        const selectedSet = new Set(currentSelection);
        const selectedCount = selectedSet.size;

        rendered.push(
          <SimCard key={component.rowKey} className="bg-surface-header p-5 sm:p-6">
            <SimLabel>Multi-select Task</SimLabel>
            <p className="mt-3 text-base font-medium text-txt-primary">
              {asString(component.payload.prompt) || 'Select all that apply'}
            </p>
            {selectExactly ? (
              <p className="mt-2 text-xs uppercase tracking-[0.16em] text-txt-muted/90">
                {selectedCount} / {selectExactly} selected
              </p>
            ) : null}
            <div className="mt-4 space-y-3">
              {options.map((option) => {
                const checked = selectedSet.has(option.key);
                const disableUnchecked = Boolean(selectExactly && !checked && selectedCount >= selectExactly);
                return (
                  <label
                    key={`${component.rowKey}:${option.key}`}
                    className={`flex items-start gap-3 rounded-input border px-3 py-2 text-sm ${
                      checked
                        ? 'border-brand-700 text-txt-primary ring-1 ring-brand-700/20 shadow-sm bg-surface-card'
                        : 'border-line bg-surface-card text-txt-secondary hover:border-line-active'
                    } ${disableUnchecked ? 'opacity-60' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disableUnchecked}
                      onChange={(event) => {
                        onInputActivity?.({ type: 'change', rowKey: component.rowKey, inputType: 'other' });
                        const nextSelection = event.target.checked
                          ? [...currentSelection, option.key]
                          : currentSelection.filter((key) => key !== option.key);
                        const deduped = Array.from(new Set(nextSelection));
                        commitAnswer(component.rowKey, deduped, (value) =>
                          setMultiSelectAnswers((prev) => ({
                            ...prev,
                            [component.rowKey]: Array.isArray(value) ? value.map((item) => String(item)) : [],
                          }))
                        );
                      }}
                      className="mt-0.5 h-4 w-4 rounded border-line bg-surface-card text-brand-700 accent-brand-700 focus:ring-brand-700"
                    />
                    <span>{option.text}</span>
                  </label>
                );
              })}
            </div>
          </SimCard>
        );
        continue;
      }

      if (taskType === 'MatchingPairs') {
        const leftItems = normalizeOptions(component.payload.leftItems);
        const rightItems = normalizeOptions(component.payload.rightItems);
        const pairExactlyRaw =
          typeof component.payload.pairExactly === 'number'
            ? component.payload.pairExactly
            : typeof component.payload.pairExactly === 'string'
              ? Number(component.payload.pairExactly)
              : null;
        const pairExactly =
          typeof pairExactlyRaw === 'number' && Number.isInteger(pairExactlyRaw) && pairExactlyRaw > 0
            ? pairExactlyRaw
            : leftItems.length;
        const currentValueRaw = (answers && answers[component.rowKey]) ?? matchingAnswers[component.rowKey] ?? { pairs: [] };
        const currentPairsRaw =
          currentValueRaw &&
          typeof currentValueRaw === 'object' &&
          !Array.isArray(currentValueRaw) &&
          Array.isArray((currentValueRaw as { pairs?: unknown }).pairs)
            ? (currentValueRaw as { pairs: unknown[] }).pairs
            : [];
        const currentPairs = currentPairsRaw
          .map((pair) => {
            if (!pair || typeof pair !== 'object' || Array.isArray(pair)) return null;
            const value = pair as Record<string, unknown>;
            const leftId = String(value.leftId ?? '').trim();
            const rightId = String(value.rightId ?? '').trim();
            if (!leftId || !rightId) return null;
            return { leftId, rightId };
          })
          .filter((pair): pair is { leftId: string; rightId: string } => Boolean(pair));

        const selectedLeftId = selectedLeftByTask[component.rowKey] || '';
        const matchedLeftSet = new Set(currentPairs.map((pair) => pair.leftId));
        const matchedRightSet = new Set(currentPairs.map((pair) => pair.rightId));

        const commitPairs = (nextPairs: Array<{ leftId: string; rightId: string }>) => {
          const nextValue = { pairs: nextPairs };
          commitAnswer(component.rowKey, nextValue, (value) =>
            setMatchingAnswers((prev) => ({
              ...prev,
              [component.rowKey]:
                value && typeof value === 'object' && !Array.isArray(value)
                  ? (value as { pairs: Array<{ leftId: string; rightId: string }> })
                  : { pairs: [] },
            }))
          );
        };

        rendered.push(
          <SimCard key={component.rowKey} className="bg-surface-header p-5 sm:p-6">
            <SimLabel>Matching Pairs Task</SimLabel>
            <p className="mt-3 text-base font-medium text-txt-primary">
              {asString(component.payload.prompt) || 'Pair each finding with the best mitigant'}
            </p>
            <p className="mt-2 text-xs uppercase tracking-[0.16em] text-txt-muted/90">
              {currentPairs.length} / {pairExactly} pairs completed
            </p>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">
                  {asString((component.payload.ui as Record<string, unknown> | undefined)?.leftTitle) || 'Findings'}
                </p>
                {leftItems.map((item) => {
                  const isSelected = selectedLeftId === item.key;
                  const isMatched = matchedLeftSet.has(item.key);
                  const disable = isMatched && !isSelected;
                  return (
                    <button
                      key={`${component.rowKey}:left:${item.key}`}
                      type="button"
                      disabled={disable}
                      onClick={() => setSelectedLeftByTask((prev) => ({ ...prev, [component.rowKey]: item.key }))}
                      className={`w-full rounded-input border px-3 py-2 text-left text-sm transition-colors ${
                        isSelected
                          ? 'border-brand-600/50 bg-brand-50 text-txt-primary'
                          : isMatched
                            ? 'cursor-not-allowed border-line bg-surface-page/20 text-txt-muted opacity-60'
                            : 'border-line bg-surface-page/40 text-txt-secondary'
                      }`}
                    >
                      <span>{item.text}</span>
                      {isMatched ? <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-txt-muted">Paired</span> : null}
                    </button>
                  );
                })}
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">
                  {asString((component.payload.ui as Record<string, unknown> | undefined)?.rightTitle) || 'Mitigants'}
                </p>
                {rightItems.map((item) => {
                  const canPair = Boolean(selectedLeftId);
                  const isUsed = matchedRightSet.has(item.key);
                  const disable = !canPair || isUsed;
                  return (
                    <button
                      key={`${component.rowKey}:right:${item.key}`}
                      type="button"
                      disabled={disable}
                      onClick={() => {
                        if (!selectedLeftId || isUsed) return;
                        const existingForLeft = currentPairs.find((pair) => pair.leftId === selectedLeftId);
                        const remaining = currentPairs.filter((pair) => pair.leftId !== selectedLeftId);
                        if (!existingForLeft && pairExactly && remaining.length >= pairExactly) return;

                        onInputActivity?.({ type: 'change', rowKey: component.rowKey, inputType: 'other' });
                        commitPairs([...remaining, { leftId: selectedLeftId, rightId: item.key }]);
                        setSelectedLeftByTask((prev) => ({ ...prev, [component.rowKey]: '' }));
                      }}
                      className={`w-full rounded-input border px-3 py-2 text-left text-sm transition-colors ${
                        disable
                          ? 'cursor-not-allowed border-line bg-surface-page/20 text-txt-muted opacity-60'
                          : 'border-line bg-surface-page/40 text-txt-secondary hover:border-line-active'
                      }`}
                    >
                      <span>{item.text}</span>
                      {isUsed ? <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-txt-muted">Paired</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">Current pairs</p>
              {currentPairs.length === 0 ? (
                <p className="text-sm text-txt-muted">No pairs selected yet.</p>
              ) : (
                currentPairs.map((pair, index) => {
                  const leftLabel = leftItems.find((item) => item.key === pair.leftId)?.text || pair.leftId;
                  const rightLabel = rightItems.find((item) => item.key === pair.rightId)?.text || pair.rightId;
                  return (
                    <div
                      key={`${component.rowKey}:pair:${pair.leftId}:${pair.rightId}:${index}`}
                      className="flex items-center justify-between rounded-input border border-line bg-surface-page/40 px-3 py-2"
                    >
                      <p className="text-sm text-txt-secondary/95">
                        {leftLabel} {'->'} {rightLabel}
                      </p>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="text-xs uppercase tracking-[0.14em] text-txt-muted hover:text-txt-primary"
                          onClick={() => {
                            setSelectedLeftByTask((prev) => ({ ...prev, [component.rowKey]: pair.leftId }));
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-xs uppercase tracking-[0.14em] text-danger hover:text-danger-text"
                          onClick={() => {
                            onInputActivity?.({ type: 'change', rowKey: component.rowKey, inputType: 'other' });
                            commitPairs(
                              currentPairs.filter(
                                (entry) => !(entry.leftId === pair.leftId && entry.rightId === pair.rightId)
                              )
                            );
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </SimCard>
        );
        continue;
      }

      if (taskType === 'BucketSort') {
        const payload = component.payload as Record<string, unknown>;
        const normalizedCorrect = normalizeBucketSortAssignments(payload.correctAssignments ?? payload.correct);
        let usedItemsFallbackFromCorrect = false;
        let usedBucketsFallbackFromCorrect = false;
        const items = (() => {
          const normalizedItems = normalizeBucketSortEntities(payload.items);
          if (normalizedItems.length > 0) return normalizedItems;
          if (Object.keys(normalizedCorrect).length === 0) return [];
          usedItemsFallbackFromCorrect = true;
          return Object.keys(normalizedCorrect).map((key, index) => ({
            key,
            label: key,
            order: index,
          }));
        })();
        const buckets = (() => {
          const normalizedBuckets = normalizeBucketSortEntities(payload.buckets);
          if (normalizedBuckets.length > 0) return normalizedBuckets;
          if (Object.values(normalizedCorrect).length === 0) return [];
          usedBucketsFallbackFromCorrect = true;
          return Array.from(new Set(Object.values(normalizedCorrect))).map((key, index) => ({
            key,
            label: key,
            order: index,
          }));
        })();
        const currentValueFromState =
          bucketSortAnswers[component.rowKey] &&
          typeof bucketSortAnswers[component.rowKey] === 'object' &&
          !Array.isArray(bucketSortAnswers[component.rowKey])
            ? (bucketSortAnswers[component.rowKey] as Record<string, string>)
            : {};
        const rawAnswersValue = answers && answers[component.rowKey];
        const value = normalizeBucketSortAssignments(
          rawAnswersValue && typeof rawAnswersValue === 'object' && !Array.isArray(rawAnswersValue)
            ? (rawAnswersValue as Record<string, unknown>)
            : onAnswersChange
              ? {}
              : currentValueFromState
        );
        const stackMode = typeof component.payload.stackMode === 'boolean' ? component.payload.stackMode : false;
        const requireAllAssigned =
          typeof component.payload.requireAllAssigned === 'boolean' ? component.payload.requireAllAssigned : true;
        const dragEnabled =
          typeof payload.dragEnabled === 'boolean'
            ? payload.dragEnabled
            : typeof payload.disableDrag === 'boolean'
              ? !payload.disableDrag
              : true;
        const warningScopeKey = `${component.rowKey}:${component.rowOrder}:${asString(payload.prompt) || 'bucket-sort'}`;

        if (
          (usedItemsFallbackFromCorrect || usedBucketsFallbackFromCorrect) &&
          !warnedBucketSortFallbackKeys.has(warningScopeKey)
        ) {
          warnedBucketSortFallbackKeys.add(warningScopeKey);
          if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
            console.warn(
              `[ScreenRenderer:BucketSort:${component.rowKey}] using last-resort fallback from correctAssignments because items/buckets were missing in authoring payload`
            );
          }
        }

        if (items.length === 0 || buckets.length === 0) {
          if (!warnedBucketSortInvalidKeys.has(warningScopeKey)) {
            warnedBucketSortInvalidKeys.add(warningScopeKey);
            if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
              console.warn(
                `[ScreenRenderer:BucketSort:${component.rowKey}] task unavailable: authoring payload missing items or buckets and no safe fallback is possible`
              );
            }
          }

          rendered.push(
            <SimCard key={component.rowKey} className="bg-surface-header p-5 sm:p-6">
              <SimLabel>Bucket Sort Task</SimLabel>
              <p className="mt-3 text-sm text-warning-text">
                BucketSort task unavailable for this screen due to missing authoring data (items or buckets).
              </p>
            </SimCard>
          );
          continue;
        }

        rendered.push(
          <SimCard key={component.rowKey} className="bg-surface-header p-5 sm:p-6">
            <SimLabel>Bucket Sort Task</SimLabel>
            <p className="mt-3 text-base font-medium text-txt-primary">
              {asString(component.payload.prompt) || 'Classify each item into one bucket.'}
            </p>
            <BucketSortTask
              rowKey={component.rowKey}
              prompt={asString(component.payload.prompt) || ''}
              items={items}
              buckets={buckets}
              stackMode={stackMode}
              requireAllAssigned={requireAllAssigned}
              dragEnabled={dragEnabled}
              value={value}
              onChange={(next) => {
                const nextValue = normalizeBucketSortAssignments(next);
                if (!onAnswersChange) {
                  setBucketSortAnswers((prev) => {
                    return {
                      ...prev,
                      [component.rowKey]: nextValue,
                    };
                  });
                }
                commitAnswer(component.rowKey, nextValue);
              }}
              onInputActivity={() => {
                onInputActivity?.({ type: 'change', rowKey: component.rowKey, inputType: 'other' });
              }}
            />
          </SimCard>
        );
        continue;
      }
    }

    rendered.push(
      <div
        key={component.rowKey}
        className="inline-flex items-center rounded-full border border-line bg-surface-card px-3 py-1 text-xs font-medium text-txt-secondary"
      >
        Unsupported component: {component.rowType}
      </div>
    );
  }

  return <div className="space-y-5">{rendered}</div>;
};

export default ScreenRenderer;
